import React from 'react';
import {View, Text} from 'react-native';
import {Controller, useWatch} from 'react-hook-form';

import {RHSeparator} from 'src/common-components/custom-ui-helpers';
import {Typography} from 'src/styles';

import PercentageSlider from 'src/hook-form/percentage-slider';
import Picker from 'src/hook-form/number-picker';
import Collapsible from 'react-native-collapsible';
import SwitchInput from 'src/hook-form/switch-input';
import {numberRange} from 'src/common-utils/nuberRange';
import {useTranslations} from 'src/providers/translation';
import {useStyle} from 'src/providers/style';
import {Cadence, ConsecutiveSessions, DayOfWeek} from 'src/hook-form-inputs';

interface Props {
  control: any;
  edit: boolean;
  values: any;
}

const TrialByTrial = ({control, edit, values}: Props) => {
  const styles = useStyle();
  const translations = useTranslations();
  const baseline = useWatch({control, name: 'baseline'});
  const showNumberPicker = useWatch({control, name: 'masteryStaff'});
  const maintenance = useWatch({control, name: 'maintenance'});
  const maintenanceCadence = useWatch({control, name: 'maintenanceCadence'});

  return (
    <>
      <Text style={Typography.H4}>{translations('measurement')}</Text>
      <RHSeparator height={20} />
      <View
        style={[
          styles.width,
          styles.overflowHidden,
          styles.border,
          styles.borderRadiusSM,
          styles.borderColorPrimary300,
          styles.padding,
        ]}>
        {edit ? (
          <Controller
            control={control}
            render={({field: {onChange, value}}) => (
              <SwitchInput
                label={'Baseline'}
                description={
                  'Correct responses needed to prove mastery and skill treatment over consecutive sessions.'
                }
                value={value}
                onChange={onChange}
              />
            )}
            name="baseline"
            defaultValue={baseline}
          />
        ) : (
          <View>
            <Text style={[Typography.H5, styles.textColorBlack]}>
              {translations('baseline')}
            </Text>
            <Text style={[Typography.P3, styles.textColorBlack]}>
              {translations('correct_responses_needed')}
            </Text>
          </View>
        )}

        <Collapsible style={styles.paddingTop} collapsed={!baseline}>
          {edit ? (
            <Controller
              control={control}
              render={({field: {onChange, value}}) => (
                <PercentageSlider
                  label={'Percent Correct'}
                  value={value}
                  onChange={onChange}
                />
              )}
              name="baselineProbes"
              defaultValue={values?.baselineProbes ?? 80}
            />
          ) : (
            <Text style={Typography.P3}>{values?.baselineProbes}%</Text>
          )}
          <RHSeparator height={20} />
          {edit ? (
            <ConsecutiveSessions.Input type={'baselineSessions'} />
          ) : (
            <ConsecutiveSessions.Display value={values?.baselineSessions} />
          )}
        </Collapsible>
      </View>

      <RHSeparator height={20} />
      <View
        style={[
          styles.width,
          styles.overflowHidden,
          styles.border,
          styles.borderRadiusSM,
          styles.borderColorPrimary300,
          styles.padding,
        ]}>
        <View
          style={[
            styles.container,
            styles.flexWrap,
            styles.justifySpaceBetween,
            styles.alignCenter,
          ]}>
          <View>
            <Text style={[Typography.H5, styles.textColorBlack]}>
              {translations('mastery_criteria')}
            </Text>
            <Text style={[Typography.P3, styles.textColorBlack]}>
              {translations('num_correct_sessions')}
            </Text>
          </View>
        </View>

        <View style={styles.paddingTop}>
          {edit ? (
            <Controller
              control={control}
              render={({field: {onChange, value}}) => (
                <PercentageSlider
                  label={'Percent Correct'}
                  value={value}
                  onChange={onChange}
                />
              )}
              name="masteryValue"
              defaultValue={values?.masteryValue ?? 80}
            />
          ) : (
            <Text style={Typography.P3}>{values?.masteryValue}%</Text>
          )}

          <RHSeparator height={20} />
          {edit ? (
            <ConsecutiveSessions.Input type={'masterySessions'} />
          ) : (
            <ConsecutiveSessions.Display value={values?.masterySessions} />
          )}
          <RHSeparator height={20} />
          {edit ? (
            <Controller
              control={control}
              render={({field: {onChange, value}}) => (
                <SwitchInput
                  label={'Staff Minimum'}
                  description={
                    'Members needed to collect and verify before mastery.'
                  }
                  value={value}
                  onChange={onChange}
                />
              )}
              name={'masteryStaff'}
              defaultValue={values?.masteryStaff}
            />
          ) : (
            <View>
              <Text style={[Typography.H5, styles.textColorBlack]}>
                {translations('staff_min')}
              </Text>
              <Text style={[Typography.P3, styles.textColorBlack]}>
                {translations('members_before_mastery')}
              </Text>
            </View>
          )}

          <Collapsible style={styles.paddingTop} collapsed={!showNumberPicker}>
            <>
              {edit ? (
                <Controller
                  control={control}
                  render={({field: {onChange, value}}) => (
                    <Picker
                      items={numberRange(2, 5)}
                      selected={value}
                      setSelected={onChange}
                      value="masteryStaffMinimum"
                    />
                  )}
                  name="masteryStaffMinimum"
                  defaultValue={values?.masteryStaffMinimum ?? 2}
                />
              ) : (
                <Text style={Typography.P3}>{values?.masteryStaffMinimum}</Text>
              )}
            </>
          </Collapsible>
        </View>
      </View>

      <RHSeparator height={20} />
      <View
        style={[
          styles.width,
          styles.overflowHidden,
          styles.border,
          styles.borderRadiusSM,
          styles.borderColorPrimary300,
          styles.padding,
        ]}>
        {edit ? (
          <Controller
            control={control}
            render={({field: {onChange, value}}) => (
              <SwitchInput
                label={'Maintenance Criteria'}
                description={
                  'Mastered targets expectations to ensure client retention.'
                }
                value={value}
                onChange={onChange}
              />
            )}
            name="maintenance"
            defaultValue={values?.maintenance}
          />
        ) : (
          <View>
            <Text style={[Typography.H5, styles.textColorBlack]}>
              {translations('maintenance_criteria')}
            </Text>
            <Text style={[Typography.P3, styles.textColorBlack]}>
              {translations('mastered_targets')}
            </Text>
          </View>
        )}

        <Collapsible style={[styles.paddingTop]} collapsed={!maintenance}>
          {edit ? (
            <Controller
              control={control}
              render={({field: {onChange, value}}) => (
                <PercentageSlider
                  label={'Percent Correct'}
                  value={value}
                  onChange={onChange}
                />
              )}
              name="maintenanceValue"
              defaultValue={values?.maintenanceValue ?? 80}
            />
          ) : (
            <Text style={Typography.P3}>{values?.maintenanceValue}%</Text>
          )}

          {edit && maintenanceCadence !== 'daily' ? (
            <ConsecutiveSessions.Input type={'maintenanceSessions'} />
          ) : maintenanceCadence !== 'daily' ? (
            <ConsecutiveSessions.Display value={values?.maintenanceSessions} />
          ) : null}

          {edit ? (
            <>
              <Cadence.Input />
              {maintenanceCadence === 'weekly' ? (
                <DayOfWeek.Input type={'maintenanceCadenceWeekly'} />
              ) : maintenanceCadence === 'monthly' ? (
                <>
                  <Text style={Typography.P3}>Day of Month</Text>
                  <Controller
                    control={control}
                    render={({field: {onChange, value}}) => (
                      <Picker
                        items={numberRange(1, 31)}
                        selected={value}
                        setSelected={onChange}
                        value="maintenanceCadenceMonthly"
                      />
                    )}
                    name="maintenanceCadenceMonthly"
                    defaultValue={values?.maintenanceCadenceMonthly ?? 1}
                  />
                </>
              ) : null}
            </>
          ) : (
            <Cadence.Display
              value={values?.maintenanceCadence}
              month={values?.maintenanceCadenceMonthly}
              day={values?.maintenanceCadenceWeekly}
            />
          )}
          <RHSeparator height={20} />
          {edit ? (
            <>
              <Text style={[Typography.P3_BOLD, styles.textColorPrimary]}>
                Allowance of Fails
              </Text>
              <Controller
                control={control}
                render={({field: {onChange, value}}) => (
                  <Picker
                    items={numberRange(0, 5)}
                    selected={value}
                    setSelected={onChange}
                    value="maintenanceAllowance"
                  />
                )}
                name="maintenanceAllowance"
                defaultValue={values?.maintenanceAllowance ?? 1}
              />
            </>
          ) : null}
        </Collapsible>
      </View>
    </>
  );
};

export default TrialByTrial;
